import { useTranslation } from 'next-i18next'
import React from 'react'
import { useForm } from 'react-hook-form'
import BaseButton from 'src/components/Buttons/BaseButton'
import BaseTextField from 'src/components/Inputs/BaseTextField'
import PhoneField from 'src/components/Inputs/PhoneField'
import { ACCESSIBLE_BLUE, ACCESSIBLE_RED, FOCUS_RING } from './claimTheme'

export interface ClaimStartValues {
  contactName: string
  contactPhone: string
  role: string
  message: string
}

interface ClaimStartFormProps {
  loading: boolean
  defaults: Partial<ClaimStartValues>
  failed: boolean
  onSubmit: (values: ClaimStartValues) => void
  /** Guest mode (no account yet): the WhatsApp is the only way back to the claimant. */
  requirePhone?: boolean
}

/**
 * These are NOT decorative ids. `BaseTextField` renders
 * `<Controller name={id} …>`, so `id` IS the react-hook-form field name — a
 * prettier id registers a phantom field, leaves `errors.<realName>`
 * permanently undefined (no message, no `aria-invalid`, submit blocked with no
 * feedback) and posts a key the GraphQL input does not accept. They double as
 * the input's stable SSR id, which is what they were introduced for.
 */
const NAME_ID = 'contactName'
const NAME_ERROR_ID = 'directory-claim-name-error'
const ROLE_ID = 'role'
const MESSAGE_ID = 'message'

/**
 * Step 1 of the claim (plan §3.1): who is asking. Four fields, only the name
 * required — the evidence ladder is what actually decides the claim, so making
 * a real operator fill a form before they can even try to prove ownership just
 * loses claims.
 *
 * `BaseTextField` never reads `fieldState`, so every field that can fail
 * validation passes `error` explicitly and renders its own message. Without
 * that the submit blocks with no border, no text and `aria-invalid=false`.
 */
const ClaimStartForm = ({
  loading,
  defaults,
  failed,
  onSubmit,
  requirePhone
}: ClaimStartFormProps) => {
  const { t } = useTranslation('directorio')
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<ClaimStartValues>({
    defaultValues: {
      contactName: defaults.contactName ?? '',
      contactPhone: defaults.contactPhone ?? '',
      role: defaults.role ?? '',
      message: ''
    },
    mode: 'onTouched'
  })
  const nameError = errors.contactName

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mt-5 flex flex-col gap-4"
    >
      <label htmlFor={NAME_ID}>
        <span className="mb-2 block text-[15px] leading-[20px] font-semibold text-ink-700">
          {t('CLAIM.FIELD_NAME')}
        </span>
        <BaseTextField
          id={NAME_ID}
          control={control}
          fullWidth
          rules={{ required: t('CLAIM.ERROR_NAME') }}
          error={!!nameError}
          slotProps={{
            htmlInput: {
              'aria-describedby': nameError ? NAME_ERROR_ID : undefined,
              'aria-invalid': !!nameError
            }
          }}
          autoComplete="name"
          className="text-base md:text-sm"
        />
      </label>
      {nameError && (
        <p
          id={NAME_ERROR_ID}
          className="-mt-2 text-xs"
          style={{ color: ACCESSIBLE_RED }}
        >
          {nameError.message ?? t('CLAIM.ERROR_NAME')}
        </p>
      )}

      <label>
        <span className="mb-2 block text-[15px] leading-[20px] font-semibold text-ink-700">
          {t('CLAIM.FIELD_PHONE')}
        </span>
        <PhoneField<ClaimStartValues>
          name="contactPhone"
          control={control}
          fullWidth
          autoComplete="tel"
          hideHelper
          required={requirePhone}
          rules={
            requirePhone ? { required: t('CLAIM.ERROR_PHONE') } : undefined
          }
        />
      </label>

      <label htmlFor={ROLE_ID}>
        <span className="mb-2 block text-[15px] leading-[20px] font-semibold text-ink-700">
          {t('CLAIM.FIELD_ROLE')}
        </span>
        <BaseTextField
          id={ROLE_ID}
          control={control}
          fullWidth
          placeholder={t('CLAIM.PLACEHOLDER_ROLE')}
          className="text-base md:text-sm"
        />
      </label>

      <label htmlFor={MESSAGE_ID}>
        <span className="mb-2 block text-[15px] leading-[20px] font-semibold text-ink-700">
          {t('CLAIM.FIELD_MESSAGE')}
        </span>
        <BaseTextField
          id={MESSAGE_ID}
          control={control}
          fullWidth
          multiline
          rows={3}
          placeholder={t('CLAIM.PLACEHOLDER_MESSAGE')}
          className="text-base md:text-sm"
        />
      </label>

      {failed && (
        <p role="alert" className="text-xs" style={{ color: ACCESSIBLE_RED }}>
          {t('CLAIM.START_FAILED')}
        </p>
      )}

      <BaseButton
        type="submit"
        variant="contained"
        customColor={ACCESSIBLE_BLUE}
        loading={loading}
        className={`self-start rounded-full ${FOCUS_RING}`}
      >
        {t('CLAIM.START_CTA')}
      </BaseButton>
    </form>
  )
}

export default React.memo(ClaimStartForm)
